/*
 * Endpoint de estado do trial v2 — GET /api/trial/state (TRIAL_V2_API.md §4).
 *
 * PRIMA NON NUOCERE: qualquer falha (404 flag off, rede, timeout, shape
 * inesperado) retorna { flagOn: false } — a app se comporta EXATAMENTE
 * como hoje (login-first). Todo o fluxo novo só liga quando o endpoint
 * responde 200.
 *
 * NÃO usa fetchWithAuth de propósito: o interceptor global de 401 limparia
 * o JWT como efeito colateral, e o de 402/429 lançaria ChatError — aqui
 * queremos falha silenciosa e fail-safe.
 */
import { API_BASE, getStoredJwt } from './client';

/** Estado normalizado do trial v2 pro frontend */
export interface TrialStateResult {
  /** true = motor trial v2 ligado no backend (endpoint respondeu 200) */
  flagOn: boolean;
  /** Pode usar o chat AGORA nessa identidade */
  active: boolean;
  /** Dia do trial (1-based; 0 = anônimo que nunca usou o chat) */
  day: number;
  /** Casos corridos no trial (pro reforço dia 3 + paywall) */
  casesCount: number;
  /** true = anônimo em dia 2-3 (ou uuid já migrado): pedir email in-chat */
  emailRequired: boolean;
  /** true = dia 4+: paywall */
  trialEnded: boolean;
}

/** Estado fail-safe — motor desligado, app igual a hoje */
export const TRIAL_STATE_OFF: TrialStateResult = {
  flagOn: false,
  active: false,
  day: 0,
  casesCount: 0,
  emailRequired: false,
  trialEnded: false,
};

/** Timeout do fetch — flag off deve resolver rápido; rede lenta não pode
 *  segurar a tela de login indefinidamente. */
const FETCH_TIMEOUT_MS = 4000;

/**
 * Consulta GET /api/trial/state.
 * Identidade: Bearer JWT (prioridade, lido do localStorage) OU X-Anon-Id.
 * Consultar o estado NÃO inicia o relógio do trial (garantia do backend).
 */
export async function fetchTrialState(anonId: string): Promise<TrialStateResult> {
  const headers: Record<string, string> = {
    'X-Trial-Surface': 'web',
  };
  const jwt = getStoredJwt();
  if (jwt) {
    headers['Authorization'] = `Bearer ${jwt}`;
  } else {
    headers['X-Anon-Id'] = anonId;
  }

  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);

  try {
    const response = await fetch(`${API_BASE}/api/trial/state`, {
      method: 'GET',
      headers,
      signal: controller.signal,
    });

    // 404 = flag desligada → fluxo atual intacto. Qualquer outro erro idem.
    if (!response.ok) return TRIAL_STATE_OFF;

    const body = (await response.json()) as {
      ok?: boolean;
      active?: boolean;
      day?: number;
      casesCount?: number;
      emailRequired?: boolean;
      trialEnded?: boolean;
    };

    if (body.ok !== true) return TRIAL_STATE_OFF;

    return {
      flagOn: true,
      active: body.active === true,
      day: typeof body.day === 'number' ? body.day : 0,
      casesCount: typeof body.casesCount === 'number' ? body.casesCount : 0,
      emailRequired: body.emailRequired === true,
      trialEnded: body.trialEnded === true,
    };
  } catch {
    // Rede/timeout/JSON inválido → fail-safe: motor off
    return TRIAL_STATE_OFF;
  } finally {
    window.clearTimeout(timeoutId);
  }
}
